import Big from 'big.js';
import { VAT_INCLUSIVE } from '~/config/constants';
import formatCurrency from './formatCurrency';

export function isNumber(value) {
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return true;
  }
  return false;
}

export function discountedPrice(item) {
  let discount = 0;

  if (!isNumber(item.price)) {
    return new Big(0);
  }
  if (item.discount_kind === 'percentage') {
    discount = item.price * (item.discount_amount / 100);
  } else {
    discount = item.discount_amount || 0;
  }
  return new Big(item.price).minus(discount);
}

export function computeLineDiscount(line) {
  if (!line.discount_kind || typeof line.discount_amount === 'undefined' || line.discount_amount === null) {
    return 0;
  }

  if (line.discount_kind === 'percentage') {
    const price = new Big(line.price || 0);
    const discountRate = new Big(line.discount_amount).div(100);
    return Number(price.times(discountRate));
  }

  return line.discount_amount;
}

// accept item/object: {price: number, quantity: integer, vat_rate: integer}
// vatIncluded: boolean
export function vatOnly(item, vatCalculation) {
  const vatIncluded = vatCalculation === VAT_INCLUSIVE;

  if (!isNumber(item.price) || !isNumber(item.quantity) || !isNumber(item.vat_rate)) {
    return 0;
  }

  const price = discountedPrice(item);

  if (vatIncluded) {
    const v = new Big(item.vat_rate);
    const res = Number(
      price
        .times(item.quantity)
        .minus(price.times(item.quantity).div(v.plus(100).div(100))),
    );
    return res;
  }

  return Number(
    price
      .times(item.quantity)
      .times(item.vat_rate)
      .div(100),
  );
}

// accept item/object: {price: number, quantity: integer, vat_rate: integer}
// vatIncluded: boolean
export function pricePlusVatNoDiscount(item, vatCalculation) {
  const vatIncluded = vatCalculation === VAT_INCLUSIVE;
  if (!isNumber(item.price) || !isNumber(item.quantity) || !isNumber(item.vat_rate)) {
    return 0;
  }

  const price = new Big(item.price);
  if (vatIncluded) {
    return Number(price.times(item.quantity));
  }
  return Number(price.times(item.quantity).plus(vatOnly(item, vatIncluded)));
}

export function pricePlusVat(item, vatCalculation) {
  const vatIncluded = vatCalculation === VAT_INCLUSIVE;

  if (!isNumber(item.price) || !isNumber(item.quantity) || !isNumber(item.vat_rate)) {
    return 0;
  }

  const lineDiscount = computeLineDiscount(item);
  const priceAfterDiscount = new Big(item.price).minus(lineDiscount);

  if (vatIncluded) {
    return Number(priceAfterDiscount.times(item.quantity));
  }
  return Number(priceAfterDiscount.times(item.quantity).plus(vatOnly(item, vatIncluded)));
}

// accept item/object: {price: number, quantity: integer, vat_rate: integer}
// vatIncluded: boolean
export function priceNoVat(item, vatCalculation) {
  const vatIncluded = vatCalculation === VAT_INCLUSIVE;
  if (!isNumber(item.price) || !isNumber(item.quantity) || !isNumber(item.vat_rate)) {
    return 0;
  }

  const lineDiscount = computeLineDiscount(item);

  const postDiscountPrice = new Big(item.price).minus(lineDiscount);

  if (!vatIncluded) {
    return postDiscountPrice.times(item.quantity);
  }
  const v = new Big(item.vat_rate);
  return Number(postDiscountPrice.times(item.quantity).div(v.plus(100).div(100)));
}

// accept list of items/objects: [{price: number, quantity: integer, vat_rate: integer}, ...]
// vatIncluded: boolean
export function sumPrice(list, vatIncluded) {
  let noVat = new Big(0);
  let vatIncl = new Big(0);

  list.forEach((item) => {
    if (item.price) {
      noVat = noVat.plus(priceNoVat(item, vatIncluded));
      vatIncl = vatIncl.plus(pricePlusVat(item, vatIncluded));
    }
  });
  return {
    noVat: Number(noVat),
    vatIncl: Number(vatIncl),
  };
}

export function calculateInvoiceDiscount(vatIncl, noVat, discountKind, discountAmount, vatCalculation) {
  let vatInclDiscount;
  let noVatDiscount;

  if (vatIncl === 0 || noVat === 0) {
    return {
      vatInclDiscount: 0,
      noVatDiscount: 0,
    };
  }

  if (discountKind === 'percentage') {
    vatInclDiscount = new Big(vatIncl).times(discountAmount).div(100);
    noVatDiscount = new Big(noVat).times(discountAmount).div(100);
  } else if (vatCalculation === 'vat_inclusive') {
    vatInclDiscount = discountAmount;
    const vatRate = new Big(vatIncl).div(noVat).minus(1);
    noVatDiscount = new Big(discountAmount).div(vatRate.plus(1));
  } else {
    vatInclDiscount = new Big(discountAmount).times(new Big(vatIncl).div(noVat));
    noVatDiscount = discountAmount;
  }

  return {
    vatInclDiscount,
    noVatDiscount,
  };
}

// accept list of items/objects: [{price: number, quantity: integer, vat_rate: integer}, ...]
// vatIncluded: boolean
export function getVatSumList(list, vatCalculation, InvoiceWideDiscountLine) {
  const vatSumList = {
    totalVatIncl: 0,
    totalNoVat: 0,
  };

  // Calculate initial VAT values for each line item
  const validLines = list.filter(item => !(item.is_discount_line === false && item.discount_amount !== 0));

  validLines.forEach((item) => {
    let vatValue; let
      itemValue;

    if (vatCalculation === 'vat_inclusive') {
      const vatRate = new Big(item.vat_rate || 0).div(100);
      const priceWithVat = new Big(discountedPrice(item)).times(item.quantity || 0);
      // Extract the VAT from the price
      vatValue = priceWithVat.minus(priceWithVat.div(vatRate.plus(1)));
      // Price without VAT
      itemValue = priceWithVat.minus(vatValue);
    } else {
      // Calculate the VAT for each item
      vatValue = new Big(vatOnly(item, vatCalculation));
      // Calculate the discounted price for each item
      itemValue = new Big(discountedPrice(item)).times(item.quantity || 0);
    }

    vatSumList.totalVatIncl += Number(itemValue.plus(vatValue));
    vatSumList.totalNoVat += Number(itemValue);

    if (item.vat_rate in vatSumList) {
      vatSumList[item.vat_rate] += Number(vatValue);
    } else if (item.vat_rate !== null) {
      vatSumList[item.vat_rate] = Number(vatValue);
    }
  });

  // Apply invoice discount if available
  if (InvoiceWideDiscountLine) {
    const invoiceDiscount = calculateInvoiceDiscount(
      vatSumList.totalVatIncl,
      vatSumList.totalNoVat,
      InvoiceWideDiscountLine.discount_kind,
      InvoiceWideDiscountLine.discount_amount,
      vatCalculation,
    );

    const totalVatInclBeforeDiscount = vatSumList.totalVatIncl;
    if (totalVatInclBeforeDiscount !== 0) {
      const discountProportion = new Big(invoiceDiscount.vatInclDiscount).div(new Big(totalVatInclBeforeDiscount));

      // Adjust each VAT category by the discount proportion
      Object.keys(vatSumList).forEach((key) => {
        if (!['totalVatIncl', 'totalNoVat'].includes(key)) {
          vatSumList[key] = new Big(vatSumList[key]).times(new Big(1).minus(discountProportion));
        }
      });

      // Adjust total values by the discount proportion
      vatSumList.totalVatIncl = new Big(vatSumList.totalVatIncl).times(new Big(1).minus(discountProportion));
      vatSumList.totalNoVat = new Big(vatSumList.totalNoVat).times(new Big(1).minus(discountProportion));
    }
  }

  // Convert Big numbers to Number for total values
  vatSumList.totalVatIncl = Number(vatSumList.totalVatIncl);
  vatSumList.totalNoVat = Number(vatSumList.totalNoVat);

  return vatSumList;
}

export function round50(value) {
  const step = 0.5;
  const inv = 1.0 / step;
  return Math.round(value * inv) / inv;
}

export function roundInt(value) {
  const valueEdited = new Big(value);
  return Number(valueEdited.round());
}

export function precision(value) {
  const decimalPlaces = 2;
  const valueEdited = new Big(value);
  return valueEdited.toFixed(decimalPlaces); // return string value with ending zeros
}

export function round(value, roundType = 'none') {
  switch (roundType) {
    case 'whole':
      return roundInt(value);
    case '50c':
      return round50(value);
    default:
      return value;
  }
}

export function priceFormat(value, state, rounded = false) {
  const price = precision(
    rounded ? round(value, state.invoiceForm.rounding_type) : value,
  );
  return formatCurrency(
    price,
    state.invoiceForm.currency,
    state.invoiceForm.language,
  );
}
